import { fetchCalls } from "../voice/voiceApi";
import { fetchMessages } from "../sms/smsApi";
import type { VoiceCall } from "../voice/types";
import type { SmsMessage } from "../sms/types";

// Phase 2.5 Reports — fetches the windowed Voice + SMS rows the analytics
// view aggregates over. Pure pass-through: no tagging, no counting. The
// view owns all derived numbers so they stay in lockstep with /voice/calls
// (both use `tagCall` from modules/voice/tagCall.ts on the same rows).
//
// Why no custom /reports worker endpoint: both `/voice/calls` and
// `/sms/messages` already expose `from`/`to` range filters, hit the same
// RLS-correct Supabase schemas the cutovers put in place, and return the
// same shape the rest of the shell already consumes. Adding a third
// endpoint would mean a second place to keep in sync with the tag taxonomy.

// Upper bound chosen to match CallsView's 2000-limit on /sms/messages and
// the plan's "30-day default window at Tribal Axe volume fits in one
// page" assumption. If we ever cross it the view surfaces a warning
// (see ReportsModule.tsx).
const PAGE_CAP = 2000;

export type ReportsPayload = {
  calls: VoiceCall[];
  messages: SmsMessage[];
  /** True if either collection hit the page cap — counts may be under. */
  truncated: boolean;
};

export async function fetchReportsData(params: {
  fromIso: string;
  toIso: string;
}): Promise<ReportsPayload> {
  const [calls, messages] = await Promise.all([
    fetchCalls({
      from: params.fromIso,
      to: params.toIso,
      limit: PAGE_CAP,
    }),
    fetchMessages({
      from: params.fromIso,
      to: params.toIso,
      limit: PAGE_CAP,
    }),
  ]);

  return {
    calls,
    messages,
    truncated: calls.length >= PAGE_CAP || messages.length >= PAGE_CAP,
  };
}
